import {
  type ClauseArticle,
  type ClauseEdits,
  type ClauseTarget,
  useClausesPreview,
  useContractClauses,
  useResendContract,
  useSaveContractClauses
} from '@services/contracts/clauses'
import {
  AlertTriangle,
  Ban,
  FileText,
  Pencil,
  Plus,
  RefreshCw,
  RotateCcw,
  Save,
  Send,
  Trash2,
  Undo2
} from 'lucide-react'
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { useConfirm } from '@/components/ui/confirm-dialog'
import { Label } from '@/components/ui/label'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from '@/components/ui/sheet'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

import { ContractMinorsSection } from './ContractMinorsSection'

type ResendOptions = {
  contractId: string
  type: 'offer' | 'flat'
  /** Tenant contract created before its SNA fee was stored. */
  askSnaFee?: boolean
}

type Props = {
  target: ClauseTarget
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Set for a contract already out for signing: offers "send new version". */
  resend?: ResendOptions
}

function draftFrom(articles: ClauseArticle[]): ClauseEdits {
  const edited: Record<string, string> = {}
  const added: Record<string, string[]> = {}
  const dropped: string[] = []
  for (const article of articles) {
    for (const point of article.points) {
      if (point.text !== null) edited[point.id] = point.text
      if (point.dropped) dropped.push(point.id)
    }
    if (article.added.length) added[article.key] = [...article.added]
  }
  return { edited, added, dropped }
}

function cleanDraft(draft: ClauseEdits): ClauseEdits {
  const edited = Object.fromEntries(
    Object.entries(draft.edited).filter(([, text]) => text.trim() !== '')
  )
  const added: Record<string, string[]> = {}
  for (const [key, texts] of Object.entries(draft.added)) {
    const kept = texts.filter((text) => text.trim() !== '')
    if (kept.length) added[key] = kept
  }
  return { edited, added, dropped: draft.dropped }
}

/**
 * Printed number of every kept point: dropped points are left out and the
 * following points of the article move up (same as the contract template).
 */
function printedNumbers(articles: ClauseArticle[], dropped: Set<string>) {
  const numbers = new Map<string, number>()
  for (const article of articles) {
    let number = 0
    for (const point of article.points) {
      if (!dropped.has(point.id)) numbers.set(point.id, ++number)
    }
  }
  return numbers
}

/** Plain override text as the contract prints it: *bold*, "- " sub-points. */
function ClauseText({ text }: { text: string }) {
  return (
    <div className="space-y-1 text-sm leading-relaxed text-ink">
      {text.split('\n').map((line, i) => {
        const sub = line.match(/^(-|[a-z]\.)\s+(.*)$/)
        const content = (sub ? (sub[2] ?? '') : line)
          .split('*')
          .map((part, j, parts) =>
            j % 2 === 1 && j < parts.length - 1 ? (
              <strong key={j}>{part}</strong>
            ) : (
              <span key={j}>{j % 2 === 1 ? `*${part}` : part}</span>
            )
          )
        return sub ? (
          <p key={i} className="m-0 flex gap-2 pl-4">
            <span className="w-4 shrink-0">{sub[1]}</span>
            <span>{content}</span>
          </p>
        ) : (
          <p key={i} className="m-0">
            {content}
          </p>
        )
      })}
    </div>
  )
}

function PointRow({
  number,
  highlighted,
  children
}: {
  /** Null for a dropped point. */
  number: number | null
  highlighted: boolean
  children: ReactNode
}) {
  return (
    <div
      className={cn(
        'flex gap-3 rounded-xl px-3 py-2.5',
        highlighted && 'bg-peach/10 ring-1 ring-peach/30'
      )}
    >
      <span className="w-7 shrink-0 pt-0.5 text-sm font-semibold text-muted-foreground">
        {number === null ? '—' : `${number}.`}
      </span>
      <div className="min-w-0 flex-1 space-y-2">{children}</div>
    </div>
  )
}

/**
 * Editor of a contract's points: rewrite a point, drop it (the following
 * points are renumbered, references to them follow), or append new points to
 * an article, with the rendered PDF alongside.
 * Everything is rendered by the backend from the same template that goes to
 * DocuSeal, so signature placement is never affected.
 */
export function ContractClausesEditor({
  target,
  open,
  onOpenChange,
  resend
}: Props) {
  const { t } = useTranslation('clauses')
  const { confirm, dialog } = useConfirm()

  const clauses = useContractClauses(target, open)
  const save = useSaveContractClauses(target)
  const preview = useClausesPreview(target)
  const resendContract = useResendContract()

  const [draft, setDraft] = useState<ClauseEdits>({
    edited: {},
    added: {},
    dropped: []
  })
  const [editing, setEditing] = useState<Set<string>>(new Set())
  const [dirty, setDirty] = useState(false)
  const [snaFee, setSnaFee] = useState(true)
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const pdfUrlRef = useRef<string | null>(null)

  const articles = clauses.data ?? []
  const dropped = new Set(draft.dropped)
  const numbers = printedNumbers(articles, dropped)
  const allPoints = articles.flatMap((article) =>
    article.points.map((point) => ({ point, article }))
  )

  // Kept, unedited points whose standard wording cites point `id` by number;
  // while there are any, the point cannot be dropped.
  const referrers = (id: string) =>
    allPoints.filter(
      ({ point }) =>
        point.refs.includes(id) &&
        !dropped.has(point.id) &&
        draft.edited[point.id] === undefined
    )

  const loadPreview = useCallback(async () => {
    try {
      const blob = await preview.mutateAsync()
      const url = URL.createObjectURL(blob)
      if (pdfUrlRef.current) URL.revokeObjectURL(pdfUrlRef.current)
      pdfUrlRef.current = url
      setPdfUrl(url)
    } catch {
      // onError already shows a toast
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview.mutateAsync])

  // Fresh draft and preview every time the editor opens (the query refetches
  // on its own when it becomes enabled).
  useEffect(() => {
    if (!open) return
    setDirty(false)
    setEditing(new Set())
    if (clauses.data) setDraft(draftFrom(clauses.data))
    void loadPreview()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (clauses.data && !dirty) setDraft(draftFrom(clauses.data))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clauses.data])

  useEffect(
    () => () => {
      if (pdfUrlRef.current) URL.revokeObjectURL(pdfUrlRef.current)
    },
    []
  )

  const update = (fn: (draft: ClauseEdits) => ClauseEdits) => {
    setDraft((current) => fn(current))
    setDirty(true)
  }

  const setPointText = (id: string, text: string | null) =>
    update((d) => {
      const edited = { ...d.edited }
      if (text === null) delete edited[id]
      else edited[id] = text
      return { ...d, edited }
    })

  const setDropped = (id: string, on: boolean) => {
    update((d) => {
      const edited = { ...d.edited }
      if (on) delete edited[id]
      return {
        ...d,
        edited,
        dropped: on
          ? [...d.dropped, id]
          : d.dropped.filter((other) => other !== id)
      }
    })
    toggleEditing(id, false)
  }

  const setAdded = (key: string, texts: string[]) =>
    update((d) => ({ ...d, added: { ...d.added, [key]: texts } }))

  const toggleEditing = (id: string, on: boolean) =>
    setEditing((current) => {
      const next = new Set(current)
      if (on) next.add(id)
      else next.delete(id)
      return next
    })

  const persist = async () => {
    const data = await save.mutateAsync(cleanDraft(draft))
    setDraft(draftFrom(data))
    setDirty(false)
    setEditing(new Set())
  }

  const handleSave = async () => {
    try {
      await persist()
    } catch {
      return
    }
    await loadPreview()
  }

  const handleResend = async () => {
    if (!resend) return
    if (
      !(await confirm({
        title: t('resend_confirm_title'),
        description: t('resend_confirm_description'),
        confirmText: t('resend'),
        cancelText: t('cancel')
      }))
    )
      return
    try {
      if (dirty) await persist()
      await resendContract.mutateAsync({
        id: resend.contractId,
        type: resend.type,
        snaFee: resend.askSnaFee ? snaFee : undefined
      })
    } catch {
      return
    }
    onOpenChange(false)
  }

  const handleOpenChange = async (next: boolean) => {
    if (
      !next &&
      dirty &&
      !(await confirm({
        title: t('discard_title'),
        description: t('discard_description'),
        confirmText: t('discard'),
        cancelText: t('cancel'),
        destructive: true
      }))
    )
      return
    onOpenChange(next)
  }

  const editedCount =
    Object.keys(draft.edited).length +
    draft.dropped.length +
    Object.values(draft.added).reduce((n, texts) => n + texts.length, 0)
  const busy = save.isPending || resendContract.isPending

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      {dialog}
      <SheetContent
        side="right"
        className="flex w-full max-w-[100vw] flex-col gap-0 p-0 sm:max-w-[96vw]"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <SheetHeader className="border-b border-ink/10 px-6 py-4 pr-14">
          <SheetTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            {t(target.kind === 'offer' ? 'title_offer' : 'title_flat')}
          </SheetTitle>
          <SheetDescription>{t('description')}</SheetDescription>
        </SheetHeader>

        <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-2">
          {/* Points */}
          <div className="min-h-0 overflow-y-auto border-ink/10 px-4 py-4 lg:border-r">
            <p className="m-0 mb-4 rounded-xl bg-ink/[0.04] px-3 py-2 text-xs text-muted-foreground">
              {t('format_hint')}
            </p>

            {target.kind === 'offer' && open && (
              <ContractMinorsSection
                offerId={target.offerId}
                onSaved={() => void loadPreview()}
              />
            )}

            {clauses.isLoading && (
              <div className="h-40 animate-pulse rounded-2xl bg-ink/[0.04]" />
            )}

            <TooltipProvider delayDuration={200}>
              <div className="space-y-6">
                {articles.map((article) => {
                  const added = draft.added[article.key] ?? []
                  const last = article.points.filter(
                    (point) => !dropped.has(point.id)
                  ).length
                  // Admin-written text keeps its numbers as typed.
                  const renumbered =
                    article.points.some((point) => dropped.has(point.id)) &&
                    (added.length > 0 ||
                      article.points.some(
                        (point) => draft.edited[point.id] !== undefined
                      ))
                  return (
                    <section key={article.key} className="space-y-1">
                      <h4 className="m-0 px-3 text-center font-display text-sm font-bold text-ink">
                        {t('article', { num: article.num })}
                        <span className="block font-normal text-muted-foreground">
                          {article.title}
                        </span>
                      </h4>

                      {renumbered && (
                        <div className="mx-3 flex items-start gap-2 rounded-lg bg-peach/10 px-3 py-2 text-xs text-ink">
                          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-peach" />
                          <p className="m-0">{t('renumbered')}</p>
                        </div>
                      )}

                      {article.points.map((point) => {
                        if (dropped.has(point.id)) {
                          return (
                            <PointRow key={point.id} number={null} highlighted>
                              <div className="text-muted-foreground line-through">
                                <ClauseText text={point.default_text} />
                              </div>
                              <div className="flex flex-wrap items-center gap-1">
                                <span className="mr-1 rounded-full bg-peach/15 px-2 py-0.5 text-xs font-semibold text-peach">
                                  {t('dropped')}
                                </span>
                                <Button
                                  size="sm"
                                  variant="ghostDark"
                                  onClick={() => setDropped(point.id, false)}
                                >
                                  <Undo2 className="h-3.5 w-3.5" />
                                  {t('restore')}
                                </Button>
                              </div>
                            </PointRow>
                          )
                        }
                        const text = draft.edited[point.id]
                        const isEdited = text !== undefined
                        const isEditing = editing.has(point.id)
                        const citedBy = point.fixed ? [] : referrers(point.id)
                        return (
                          <PointRow
                            key={point.id}
                            number={numbers.get(point.id) ?? point.number}
                            highlighted={isEdited}
                          >
                            {isEditing ? (
                              <Textarea
                                autoFocus
                                rows={Math.min(
                                  14,
                                  Math.max(
                                    4,
                                    Math.ceil((text ?? '').length / 70)
                                  )
                                )}
                                value={text ?? point.default_text}
                                onChange={(e) =>
                                  setPointText(point.id, e.target.value)
                                }
                              />
                            ) : (
                              <ClauseText text={text ?? point.default_text} />
                            )}

                            {isEdited && point.stale && (
                              <div className="flex items-start gap-2 rounded-lg bg-destructive/[0.06] px-3 py-2 text-xs text-ink">
                                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" />
                                <div className="space-y-1">
                                  <p className="m-0 font-semibold">
                                    {t('stale')}
                                  </p>
                                  <ClauseText text={point.default_text} />
                                </div>
                              </div>
                            )}

                            <div className="flex flex-wrap items-center gap-1">
                              {isEdited && (
                                <span className="mr-1 rounded-full bg-peach/15 px-2 py-0.5 text-xs font-semibold text-peach">
                                  {t('edited')}
                                </span>
                              )}
                              {isEditing ? (
                                <Button
                                  size="sm"
                                  variant="ghostDark"
                                  onClick={() => toggleEditing(point.id, false)}
                                >
                                  {t('done')}
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="ghostDark"
                                  onClick={() => {
                                    if (!isEdited)
                                      setPointText(point.id, point.default_text)
                                    toggleEditing(point.id, true)
                                  }}
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                  {t('edit')}
                                </Button>
                              )}
                              {!point.fixed &&
                                (citedBy.length === 0 ? (
                                  <Button
                                    size="sm"
                                    variant="ghostDark"
                                    onClick={() => setDropped(point.id, true)}
                                  >
                                    <Ban className="h-3.5 w-3.5" />
                                    {t('drop')}
                                  </Button>
                                ) : (
                                  <Tooltip>
                                    {/* Disabled buttons don't fire hover events,
                                      so the wrapping span is the trigger. */}
                                    <TooltipTrigger asChild>
                                      <span tabIndex={0}>
                                        <Button
                                          size="sm"
                                          variant="ghostDark"
                                          disabled
                                        >
                                          <Ban className="h-3.5 w-3.5" />
                                          {t('drop')}
                                        </Button>
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-xs">
                                      {t('drop_referenced', {
                                        points: citedBy
                                          .map(({ point: p, article: a }) =>
                                            t('point_ref', {
                                              article: a.num,
                                              number: numbers.get(p.id)
                                            })
                                          )
                                          .join(', ')
                                      })}
                                    </TooltipContent>
                                  </Tooltip>
                                ))}
                              {isEdited && (
                                <Button
                                  size="sm"
                                  variant="ghostDark"
                                  onClick={() => {
                                    setPointText(point.id, null)
                                    toggleEditing(point.id, false)
                                  }}
                                >
                                  <RotateCcw className="h-3.5 w-3.5" />
                                  {t('reset')}
                                </Button>
                              )}
                            </div>
                          </PointRow>
                        )
                      })}

                      {added.map((value, index) => (
                        <PointRow
                          key={`${article.key}-added-${index}`}
                          number={last + index + 1}
                          highlighted
                        >
                          <Textarea
                            rows={3}
                            value={value}
                            placeholder={t('new_point_placeholder')}
                            onChange={(e) =>
                              setAdded(
                                article.key,
                                added.map((v, i) =>
                                  i === index ? e.target.value : v
                                )
                              )
                            }
                          />
                          <div className="flex items-center gap-1">
                            <span className="mr-1 rounded-full bg-peach/15 px-2 py-0.5 text-xs font-semibold text-peach">
                              {t('added')}
                            </span>
                            <Button
                              size="sm"
                              variant="ghostDark"
                              onClick={() =>
                                setAdded(
                                  article.key,
                                  added.filter((_, i) => i !== index)
                                )
                              }
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                              {t('remove')}
                            </Button>
                          </div>
                        </PointRow>
                      ))}

                      <div className="px-3">
                        <Button
                          size="sm"
                          variant="ghostDark"
                          onClick={() => setAdded(article.key, [...added, ''])}
                        >
                          <Plus className="h-3.5 w-3.5" />
                          {t('add_point')}
                        </Button>
                      </div>
                    </section>
                  )
                })}
              </div>
            </TooltipProvider>
          </div>

          {/* Preview */}
          <div className="hidden min-h-0 flex-col bg-ink/[0.03] lg:flex">
            <div className="flex items-center justify-between gap-2 px-4 py-2 text-xs text-muted-foreground">
              <span>{dirty ? t('preview_outdated') : t('preview_hint')}</span>
              <Button
                size="sm"
                variant="ghostDark"
                onClick={() => void loadPreview()}
                disabled={preview.isPending}
              >
                <RefreshCw
                  className={cn(
                    'h-3.5 w-3.5',
                    preview.isPending && 'animate-spin'
                  )}
                />
                {t('refresh_preview')}
              </Button>
            </div>
            {pdfUrl ? (
              <iframe
                title={t('preview')}
                src={pdfUrl}
                className={cn(
                  'min-h-0 w-full flex-1 border-0 transition-opacity',
                  preview.isPending && 'opacity-50'
                )}
              />
            ) : (
              <div className="m-4 flex-1 animate-pulse rounded-2xl bg-ink/[0.05]" />
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-ink/10 px-6 py-3">
          <span className="text-sm text-muted-foreground">
            {editedCount > 0
              ? t('edited_count', { count: editedCount })
              : t('standard')}
            {dirty && ` · ${t('unsaved')}`}
          </span>
          <div className="flex flex-wrap items-center gap-2">
            {resend?.askSnaFee && (
              <label className="mr-2 flex items-center gap-2 text-sm">
                <Switch checked={snaFee} onCheckedChange={setSnaFee} />
                <Label>{t('sna_fee')}</Label>
              </label>
            )}
            <Button
              variant={resend ? 'ghostDark' : 'default'}
              onClick={handleSave}
              disabled={!dirty || busy}
            >
              <Save className="h-4 w-4" />
              {t('save')}
            </Button>
            {resend && (
              <Button onClick={handleResend} disabled={busy}>
                <Send className="h-4 w-4" />
                {t(dirty ? 'save_and_resend' : 'resend')}
              </Button>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}
